<template>
  <div
    ref="rowRef"
    class="layer-row flex items-stretch border border-border rounded-md overflow-hidden cursor-pointer select-none relative transition-all duration-200 ease-out"
    :class="rowClasses"
    :style="dragTransformStyle"
    :draggable="true"
    @click="$emit('select', layer.id)"
    @contextmenu.prevent="openContextMenu"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @dragover.prevent="handleDragOver"
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <!-- Thumbnail - no padding, flush left -->
    <div 
      class="w-12 h-12 flex-shrink-0 bg-muted flex items-center justify-center"
      :class="{ 'ring-2 ring-inset ring-orange-400': layer.is_inherited }"
    >
      <img 
        v-if="layer.type === 'image' && thumbnailUrl"
        :src="thumbnailUrl" 
        :alt="layer.name"
        class="w-full h-full object-cover"
        @error="handleImageError"
        draggable="false"
      />
      <component 
        v-else 
        :is="layerIcon" 
        class="w-5 h-5 text-muted-foreground" 
      />
    </div>

    <!-- Text Section - middle -->
    <div class="flex-1 min-w-0 px-3 flex flex-col justify-center border-l border-border">
      <p 
        class="text-sm font-medium truncate"
        :class="{ 'text-orange-600': layer.is_inherited }"
      >
        {{ layer.name || 'Untitled' }}
      </p>
      <p v-if="layer.is_inherited" class="text-[10px] text-orange-500 truncate">
        From {{ layer.parent_scene_name || 'Parent' }}
      </p>
    </div>

    <!-- Icons Grid - right side, bordered section with 2 rows -->
    <div class="flex-shrink-0 border-l border-border flex flex-col w-10 h-12">
      <!-- Eye Icon -->
      <div
        class="h-6 flex items-center justify-center hover:bg-accent transition-colors cursor-pointer"
        :class="{ 'opacity-40': layer.visible === false }"
        @click.stop="$emit('toggle-visibility', layer)"
        :title="layer.visible === false ? 'Show layer' : 'Hide layer'"
        role="button"
      >
        <svg v-if="layer.visible !== false" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/></svg>
      </div>
      
      <!-- Divider -->
      <div class="border-t border-border" />
      
      <!-- Lock Icon -->
      <div
        class="h-6 flex items-center justify-center hover:bg-accent transition-colors cursor-pointer"
        @click.stop="$emit('toggle-lock', layer)"
        :title="layer.locked ? 'Unlock layer' : 'Lock layer'"
        role="button"
      >
        <svg v-if="layer.locked" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
      </div>
    </div>

    <!-- Drop Indicator -->
    <div 
      v-if="dropPosition === 'before'"
      class="absolute -top-0.5 left-0 right-0 h-0.5 bg-primary rounded-full z-10"
    />
    <div 
      v-if="dropPosition === 'after'"
      class="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-primary rounded-full z-10"
    />
  </div>

  <!-- Context Menu (invisible dropdown triggered by ref) -->
  <DropdownMenu v-model:open="contextMenuOpen">
    <DropdownMenuTrigger as-child>
      <span ref="contextMenuTrigger" class="hidden" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" class="w-40" :style="contextMenuStyle">
      <DropdownMenuItem 
        v-if="layer.is_inherited"
        @click="$emit('reset', layer.id)"
        class="text-orange-600"
      >
        <RotateCcw class="w-4 h-4 mr-2" />
        Reset to Parent
      </DropdownMenuItem>
      <DropdownMenuItem 
        v-if="!layer.is_inherited"
        @click="$emit('delete', layer.id)"
        class="text-destructive"
      >
        <Trash2 class="w-4 h-4 mr-2" />
        Delete Layer
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Eye, EyeOff, Lock, Unlock, Trash2, ImageIcon, Video, Youtube, Type, RotateCcw } from 'lucide-vue-next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { DisplayLayer } from '@/stores/sceneDisplayConfigs'
import { useImageCacheStore } from '@/stores/imageCache'
import { useImageLibraryStore } from '@/stores/imageLibrary'
import { logger } from '@/lib/logger'
import { reactive } from 'vue'

// Shared drag state across all LayerRow instances (module-level reactive)
// This allows all LayerRow components to react to drag position changes
const sharedDragState = reactive({
  isDragging: false,
  sourceIndex: null as number | null,
  targetIndex: null as number | null,
  targetPosition: null as 'before' | 'after' | null,
})

interface Props {
  layer: DisplayLayer
  isSelected: boolean
  index: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  select: [layerId: string]
  'toggle-visibility': [layer: DisplayLayer]
  'toggle-lock': [layer: DisplayLayer]
  delete: [layerId: string]
  reset: [layerId: string]
  reorder: [fromIndex: number, toIndex: number]
}>()

const imageCacheStore = useImageCacheStore()
const imageLibraryStore = useImageLibraryStore()

// Track retries to prevent infinite loops
const imageRetryCount = ref(0)
const MAX_IMAGE_RETRIES = 2

const rowRef = ref<HTMLElement>()
const contextMenuTrigger = ref<HTMLSpanElement>()
const contextMenuOpen = ref(false)
const contextMenuStyle = ref({})
const dropPosition = ref<'before' | 'after' | null>(null)

// Open context menu at mouse position
const openContextMenu = (event: MouseEvent) => {
  contextMenuStyle.value = {
    position: 'fixed',
    left: `${event.clientX}px`,
    top: `${event.clientY}px`,
  }
  contextMenuOpen.value = true
}

// Computed classes for row styling
const rowClasses = computed(() => ({
  'bg-primary/10 border-primary': props.isSelected && !props.layer.is_inherited,
  'bg-orange-500/20 border-orange-500': props.isSelected && props.layer.is_inherited,
  'hover:border-muted-foreground/40': !props.isSelected,
  'opacity-60': props.layer.visible === false,
  'opacity-40 scale-95': sharedDragState.isDragging && sharedDragState.sourceIndex === props.index, // Dim the dragged item
}))

// Computed style for drag animation - shifts rows to "make space" for the dragged item
const dragTransformStyle = computed(() => {
  // Not dragging or this is the source item
  if (!sharedDragState.isDragging || sharedDragState.sourceIndex === null || sharedDragState.targetIndex === null) {
    return {}
  }
  
  const sourceIdx = sharedDragState.sourceIndex
  const targetIdx = sharedDragState.targetIndex
  const position = sharedDragState.targetPosition
  
  // Don't transform the source item itself
  if (props.index === sourceIdx) {
    return {}
  }
  
  // Calculate effective target position
  const effectiveTarget = position === 'after' ? targetIdx + 1 : targetIdx
  
  // Row height + gap (48px + 4px gap)
  const shiftAmount = 52
  
  // Determine if this row should shift
  if (sourceIdx < targetIdx) {
    // Dragging downward: items between source+1 and target should shift UP
    if (props.index > sourceIdx && props.index <= targetIdx) {
      return { transform: `translateY(-${shiftAmount}px)` }
    }
  } else if (sourceIdx > targetIdx) {
    // Dragging upward: items between target and source-1 should shift DOWN
    if (props.index >= effectiveTarget && props.index < sourceIdx) {
      return { transform: `translateY(${shiftAmount}px)` }
    }
  }
  
  return {}
})

// Get the appropriate icon for the layer type
const layerIcon = computed(() => {
  switch (props.layer.type) {
    case 'image': return ImageIcon
    case 'video': return Video
    case 'youtube': return Youtube
    case 'text': return Type
    default: return ImageIcon
  }
})

// Get cached thumbnail URL
const thumbnailUrl = computed(() => {
  if (props.layer.type !== 'image') return ''
  
  const image = imageLibraryStore.images.find(img => img.id === props.layer.content_source)
  const storagePath = image?.preview_storage_path || image?.storage_path || props.layer.storage_path
  
  if (storagePath) {
    return imageCacheStore.getCachedUrlByPath(storagePath)
  }
  
  return props.layer.content ? imageCacheStore.getCachedUrl(props.layer.content) : ''
})

// Handle image error - invalidate cache and retry (with limit)
const handleImageError = async (event: Event) => {
  const img = event.target as HTMLImageElement
  const originalSrc = img.src
  
  if (originalSrc.startsWith('blob:')) {
    // Limit retries to prevent infinite loops (Firefox security error issue)
    if (imageRetryCount.value >= MAX_IMAGE_RETRIES) {
      logger.warn('[LayerRow] Max retries reached, stopping retry loop')
      return
    }
    
    imageRetryCount.value++
    logger.warn(`[LayerRow] Blob URL failed, retry ${imageRetryCount.value}/${MAX_IMAGE_RETRIES}`)
    
    const image = imageLibraryStore.images.find(i => i.id === props.layer.content_source)
    const storagePath = image?.preview_storage_path || image?.storage_path || props.layer.storage_path
    
    if (storagePath) {
      await imageCacheStore.invalidateByPath(storagePath)
      // Force re-fetch
      await imageCacheStore.getImageByPath(storagePath)
    }
  }
}

// Drag and Drop handling
let draggedIndex: number | null = null

const handleDragStart = (event: DragEvent) => {
  if (!event.dataTransfer) return
  
  draggedIndex = props.index
  sharedDragState.isDragging = true
  sharedDragState.sourceIndex = props.index
  sharedDragState.targetIndex = null
  sharedDragState.targetPosition = null
  
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', String(props.index))
  event.dataTransfer.setData('application/x-layer-index', String(props.index))
  
  logger.info('[LayerRow] Drag started from index:', props.index)
  
  // Add dragging class
  if (rowRef.value) {
    rowRef.value.classList.add('opacity-50')
  }
}

const handleDragEnd = () => {
  logger.info('[LayerRow] Drag ended, shared state:', {
    source: sharedDragState.sourceIndex,
    target: sharedDragState.targetIndex,
    position: sharedDragState.targetPosition
  })
  
  // Use shared state to perform reorder (since drop event may not fire due to Tauri)
  if (sharedDragState.sourceIndex !== null && sharedDragState.targetIndex !== null) {
    const fromIndex = sharedDragState.sourceIndex
    let toIndex = sharedDragState.targetIndex
    
    // Adjust toIndex based on drop position
    if (sharedDragState.targetPosition === 'after') {
      toIndex = toIndex + 1
    }
    
    logger.info('[LayerRow] Reorder from', fromIndex, 'to', toIndex, '(via dragend)')
    
    // Emit reorder if indices are different
    if (fromIndex !== toIndex && fromIndex !== toIndex - 1) {
      emit('reorder', fromIndex, toIndex > fromIndex ? toIndex - 1 : toIndex)
    }
  }
  
  // Reset state
  dropPosition.value = null
  draggedIndex = null
  sharedDragState.isDragging = false
  sharedDragState.sourceIndex = null
  sharedDragState.targetIndex = null
  sharedDragState.targetPosition = null
  
  if (rowRef.value) {
    rowRef.value.classList.remove('opacity-50')
  }
}

const handleDragOver = (event: DragEvent) => {
  if (!event.dataTransfer) return
  
  // Check if this is a layer reorder drag
  const hasLayerData = event.dataTransfer.types.includes('application/x-layer-index')
  if (!hasLayerData) {
    return
  }
  
  event.dataTransfer.dropEffect = 'move'
  
  // Determine drop position based on mouse Y within the element
  if (rowRef.value) {
    const rect = rowRef.value.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    
    const position = event.clientY < midY ? 'before' : 'after'
    dropPosition.value = position
    
    // Update shared state for dragend handler and animation
    if (sharedDragState.targetIndex !== props.index || sharedDragState.targetPosition !== position) {
      sharedDragState.targetIndex = props.index
      sharedDragState.targetPosition = position
      logger.debug('[LayerRow] DragOver updated target:', { index: props.index, position })
    }
  }
}

const handleDragLeave = () => {
  dropPosition.value = null
  // Don't clear shared state here - we want to keep the last valid target
}

const handleDrop = (event: DragEvent) => {
  logger.info('[LayerRow] Drop event on index:', props.index)
  
  if (!event.dataTransfer) {
    logger.warn('[LayerRow] No dataTransfer')
    return
  }
  
  const fromIndexStr = event.dataTransfer.getData('application/x-layer-index')
  logger.info('[LayerRow] FromIndex string:', fromIndexStr)
  
  if (!fromIndexStr) {
    dropPosition.value = null
    return
  }
  
  const fromIndex = parseInt(fromIndexStr, 10)
  let toIndex = props.index
  
  // Adjust toIndex based on drop position
  if (dropPosition.value === 'after') {
    toIndex = props.index + 1
  }
  
  logger.info('[LayerRow] Reorder from', fromIndex, 'to', toIndex, '(via drop)')
  
  // Emit reorder if indices are different
  if (fromIndex !== toIndex && fromIndex !== toIndex - 1) {
    emit('reorder', fromIndex, toIndex > fromIndex ? toIndex - 1 : toIndex)
  }
  
  // Clear shared state so dragend doesn't double-fire the reorder
  sharedDragState.isDragging = false
  sharedDragState.sourceIndex = null
  sharedDragState.targetIndex = null
  sharedDragState.targetPosition = null
  
  dropPosition.value = null
}
</script>

<style scoped>
.layer-row {
  min-height: 48px;
}
</style>
